import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

const ChatModal = ({ recipientId, recipientName, taskId, taskTitle, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [currentTaskTitle, setCurrentTaskTitle] = useState(taskTitle || '');
  const [currentTaskId, setCurrentTaskId] = useState(taskId || null);
  
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  const fetchMessages = async () => {
    const token = localStorage.getItem('token');
    if (!token || !recipientId) return;

    try {
      const res = await fetch(`/api/messages/chat/${recipientId}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
        
        if (!currentTaskTitle && data.length > 0) {
          const msgWithTask = data.slice().reverse().find(m => m.task || m.task_id || m.task_title);
          if (msgWithTask) {
            if (msgWithTask.task_title) setCurrentTaskTitle(msgWithTask.task_title);
            if (msgWithTask.task_id || msgWithTask.task) setCurrentTaskId(msgWithTask.task_id || msgWithTask.task);
          }
        }

        setTimeout(scrollToBottom, 50);
      }
    } catch (err) {
      console.error('Ошибка загрузки переписки:', err);
    }
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    fetchMessages();
    const interval = setInterval(fetchMessages, 4000);
    
    return () => {
      document.body.style.overflow = 'unset';
      clearInterval(interval);
    };
  }, [recipientId]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;

    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/messages/chat/${recipientId}/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text, task_id: currentTaskId || taskId })
      });

      if (res.ok) {
        setText('');
        fetchMessages();
      }
    } catch (err) {
      console.error('Ошибка отправки:', err);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const displayTitle = currentTaskTitle || taskTitle;
  const displayId = currentTaskId || taskId;

  return ReactDOM.createPortal(
    <div 
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[999999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 min-h-screen w-screen font-[-apple-system,BlinkMacSystemFont,'Plus_Jakarta_Sans','Inter',sans-serif] animate-in fade-in duration-200"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      {/* Главная карточка в стиле One UI */}
      <div className="bg-[#0b0f19]/90 border border-white/10 rounded-[32px] w-full max-w-lg shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9)] flex flex-col overflow-hidden h-[560px] max-h-[90vh] my-auto backdrop-blur-2xl relative">
        
        {/* Фоновое свечение на заднем плане карточки */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-32 bg-blue-600/10 blur-[60px] pointer-events-none rounded-full" />

        {/* Шапка модалки в стиле One UI */}
        <div className="p-5 bg-black/20 border-b border-white/10 flex justify-between items-center shrink-0 z-10">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-500 flex items-center justify-center text-white font-extrabold text-sm shadow-[0_0_15px_rgba(37,99,235,0.4)]">
                {(recipientName || 'U').charAt(0).toUpperCase()}
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 border-2 border-[#0b0f19] rounded-full shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
            </div>

            <div>
              <h3 className="font-extrabold text-white text-base tracking-tight leading-snug">
                @{recipientName || 'пользователь'}
              </h3>
              {displayTitle ? (
                <p className="text-[11px] font-semibold text-slate-400 truncate max-w-[260px] sm:max-w-[320px] flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                  <span>Задача:</span>
                  <span className="text-slate-200 font-bold italic">«{displayTitle}»</span>
                </p>
              ) : displayId ? (
                <p className="text-[11px] font-semibold text-slate-400 flex items-center gap-1 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                  <span>По задаче #{displayId}</span>
                </p>
              ) : (
                <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest">Онлайн</span>
              )}
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold w-9 h-9 flex items-center justify-center rounded-2xl transition-all cursor-pointer active:scale-90"
          >
            ✕
          </button>
        </div>

        {/* Лента сообщений */}
        <div className="flex-1 p-5 overflow-y-auto space-y-3.5 bg-[#070a12]/60 scrollbar-thin scrollbar-thumb-white/10">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-6">
              <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-2xl mb-3">
                💬
              </div>
              <p className="text-xs font-bold text-slate-300">Сообщений пока нет</p>
              <p className="text-[11px] font-medium text-slate-500 mt-1 max-w-[220px]">
                Напишите первое сообщение, чтобы обсудить детали задачи.
              </p>
            </div>
          ) : (
            messages.map((msg) => {
              const isMe = msg.sender !== recipientId && msg.sender_id !== recipientId;
              return (
                <div
                  key={msg.id || Math.random()}
                  className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[82%] p-3.5 rounded-[22px] text-[13px] font-medium leading-relaxed break-words shadow-lg transition-all ${
                      isMe
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-br-md border border-white/20 shadow-[0_4px_20px_rgba(37,99,235,0.35)]'
                        : 'bg-[#151c2c]/80 backdrop-blur-md text-slate-100 rounded-bl-md border border-white/10 shadow-[0_4px_15px_rgba(0,0,0,0.3)]'
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Форма ввода (One UI Pill) */}
        <form onSubmit={handleSend} className="p-3.5 bg-black/40 border-t border-white/10 flex items-center gap-2.5 shrink-0 backdrop-blur-md">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Сообщение..."
            className="flex-1 bg-[#121826] border border-white/10 text-white text-xs font-medium rounded-2xl px-4 py-3 focus:outline-none focus:border-blue-500/80 focus:ring-1 focus:ring-blue-500/50 transition-all placeholder:text-slate-500 shadow-inner"
          />
          <button
            type="submit"
            disabled={!text.trim()}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-30 text-white px-5 py-3 rounded-2xl font-extrabold text-xs transition-all active:scale-95 cursor-pointer shrink-0 shadow-[0_0_15px_rgba(37,99,235,0.4)] border border-white/20"
          >
            Отправить
          </button>
        </form>

      </div>
    </div>,
    document.body
  );
};

export default ChatModal;